'use client'

import { Button, Title, Text, Stack } from '@mantine/core'
import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { Avatar } from './Avatar'
import { UserWithMentor } from '@/types/mentor'
import Link from 'next/link'
import ScrollHint from './ScrollHint'

interface BecomeAMentorHeroProps {
  floatingMentors: UserWithMentor[]
}

export default function BecomeAMentorHero({ floatingMentors }: BecomeAMentorHeroProps) {
  const [scrollY, setScrollY] = useState(0)
  const [animationTime, setAnimationTime] = useState(0)
  const [yellowSectionBottom, setYellowSectionBottom] = useState(0)
  const [showFloatingAvatars, setShowFloatingAvatars] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const animate = () => {
      setAnimationTime(Date.now())
      requestAnimationFrame(animate)
    }
    const animationId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationId)
  }, [])

  useLayoutEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setYellowSectionBottom(rect.bottom + window.scrollY)
    }
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowFloatingAvatars(true)
    }, 100)
    return () => clearTimeout(timeoutId)
  }, [])

  useEffect(() => {
    const updateYellowSectionBottom = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        setYellowSectionBottom(rect.bottom + window.scrollY)
      }
    }

    window.addEventListener('resize', updateYellowSectionBottom)
    return () => window.removeEventListener('resize', updateYellowSectionBottom)
  }, [])

  return (
    <div ref={containerRef} style={{ position: 'relative', minHeight: '70vh' }}>
      {/* Floating mentor photos */}
      {showFloatingAvatars && floatingMentors.map((mentor, index) => {
        const speed = 10 + (index * 2)
        const animationOffset = index * 2000

        const yellowBottom = yellowSectionBottom > 0 ? yellowSectionBottom : 500
        const startY = yellowBottom

        const totalDistance = startY + 200
        const cycleDuration = (totalDistance / speed) * 1000

        const elapsedTime = (animationTime + animationOffset) % cycleDuration
        const progress = elapsedTime / cycleDuration

        const currentY = startY - (progress * totalDistance)

        // Match homepage opacity: max 0.25 with distance-based fading
        let opacity = 0.25
        if (currentY > startY - 100) {
          opacity = Math.max(0, (startY - currentY) / 100) * 0.25
        } else if (currentY < 100) {
          opacity = Math.max(0, currentY / 100) * 0.25
        }

        const sizeVariants = [140, 100, 120, 90, 110, 130, 95, 115, 105, 125, 135, 85]
        const size = sizeVariants[index % sizeVariants.length]

        // Match homepage depth: size-based blur and z-index
        const sizeNormalized = (size - 80) / (130 - 80)
        const blurAmount = Math.round(sizeNormalized * 16)
        const photoZIndex = Math.round(5 - (sizeNormalized * 5))

        const positions = [15, 25, 35, 45, 55, 65, 75, 85, 20, 30, 40, 60]
        const leftPosition = positions[index % positions.length]

        return (
          <div
            key={mentor.id}
            style={{
              position: 'absolute',
              left: `${leftPosition}%`,
              top: `${currentY}px`,
              opacity: opacity,
              transition: 'opacity 0.3s ease',
              pointerEvents: 'none',
              zIndex: photoZIndex,
              filter: `blur(${blurAmount}px)`,
            }}
          >
            <Avatar
              userId={mentor.id}
              avatarId={mentor.avatarId}
              size={size}
              radius="xl"
              loading="lazy"
              priority={false}
            />
          </div>
        )
      })}

      {/* Main content */}
      <div style={{ position: 'relative', zIndex: 20 }}>
        <Stack align="center" gap="xl" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
          paddingLeft: 'clamp(1rem, 4vw, 2rem)',
          paddingRight: 'clamp(1rem, 4vw, 2rem)',
          paddingBottom: 'clamp(2rem, 4vh, 3rem)',
          paddingTop: 'clamp(2rem, 4vh, 3rem)'
        }}>
          <Title
            size="clamp(2rem, 7vw, 3.5rem)"
            fw={700}
            className="gradient-emerald"
            maw={900}
            style={{ lineHeight: 1.15, letterSpacing: '-0.02em' }}
          >
            Focus on impact, not admin.
          </Title>

          <Text
            size="xl"
            c="dimmed"
            fw={500}
            maw={700}
            style={{ lineHeight: 1.6, marginTop: '-0.5rem' }}
          >
            Keep the meaningful conversations while we handle scheduling, payments, notes, and follow-ups. Better mentoring with less busy work.
          </Text>

          <Link href="/mentoring/apply" style={{ textDecoration: 'none' }}>
            <Button
              variant="filled"
              color="navy"
              size="lg"
              radius="md"
              style={{ fontWeight: 600, marginTop: '0.5rem' }}
            >
              Apply to Become a Mentor
            </Button>
          </Link>
        </Stack>
      </div>
      
      {/* Animated scroll hint */}
      <ScrollHint scrollY={scrollY} />
    </div>
  )
}
